import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion, useInView } from 'framer-motion';

export function Marquee({ children, duration = 32, className = '' }) {
  return (
    <div className={`overflow-hidden select-none ${className}`} aria-hidden="true">
      <div className="marquee-track flex w-max items-center" style={{ '--marquee-duration': `${duration}s` }}>
        <div className="flex items-center shrink-0">{children}</div>
        <div className="flex items-center shrink-0">{children}</div>
      </div>
    </div>
  );
}

export function CountUp({ to, suffix = '', prefix = '', duration = 1.8, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px 0px' });
  const reduce = useReducedMotion();
  const [val, setVal] = useState(reduce ? to : 0);
  useEffect(() => {
    if (!inView || reduce) return;
    let raf; const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / (duration * 1000), 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration, reduce]);
  return <span ref={ref} className={className}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

export function ScrollFloat({ children, from = 60, to = -60, className = '', style = {} }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [from, to]);
  if (reduce) return <div ref={ref} className={className} style={style}>{children}</div>;
  return <motion.div ref={ref} className={className} style={{ ...style, y }}>{children}</motion.div>;
}

export function RouteLine({ d, viewBox, className = '', strokeWidth = 3, dots = [] }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 90%', 'end 45%'] });
  return (
    <svg ref={ref} viewBox={viewBox} fill="none" className={className} aria-hidden="true" preserveAspectRatio="none">
      <path d={d} stroke="currentColor" strokeOpacity="0.12" strokeWidth={strokeWidth} strokeDasharray="6 10" strokeLinecap="round" />
      <motion.path
        d={d} stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round"
        style={reduce ? {} : { pathLength: scrollYProgress }}
        initial={reduce ? {} : { pathLength: 0 }}
      />
      {dots.map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={strokeWidth * 2} fill="currentColor" />
      ))}
    </svg>
  );
}

export function MaskReveal({ children, className = '', delay = 0 }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ clipPath: 'inset(0 0 100% 0)' }}
      whileInView={{ clipPath: 'inset(0 0 0% 0)' }}
      viewport={{ once: true, margin: '-80px 0px' }}
      transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

export function MouseParallax({ children, strength = 18, className = '' }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * strength;
      const y = (e.clientY / window.innerHeight - 0.5) * strength;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [strength, reduce]);
  return <div ref={ref} className={className} style={{ willChange: 'transform', transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1)' }}>{children}</div>;
}
